/**
 * Public Content API Client
 *
 * API Endpoints:
 * - GET /public/videos → PaginatedResponse<Video>
 * - GET /public/map-videos → Video[]
 * - GET /public/public-videos → paginated public videos
 * - GET /public/public-videos → PublicVideosResponse
 * - GET /public/videos/{uuid}/play → PublicVideoPlayResponse
 */

import type { Course, CourseBundle, PaginatedResponse, Video } from '../types';
import type { PublicVideosResponse, PublicVideoPlayResponse } from '@/types/public';
import { captureError } from '@/shared/lib/errors/capture';
import { apiClient } from './base-client';

export interface PublicVideosFilters extends Record<string, unknown> {
  page?: number;
}

export interface TagOption {
  id: number;
  name: string;
  slug: string;
  count: number;
}

export interface MapVideosFilters extends Record<string, unknown> {
  page?: number | undefined;
  per_page?: number | undefined;
  types?: string | undefined;
  search?: string | undefined;
  channel_id?: number | undefined;
  countries?: string[] | undefined;
  tag_ids?: number[] | undefined;
  sort?: string | undefined;
  auth?: boolean | undefined;
  compact?: boolean | undefined;
}

export interface MapVideosPaginatedResponse {
  videos: Video[];
  pagination: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface CountriesResponse {
  data: { id: number; name: string; iso: string; emoji: string; videos_count: number }[];
}

export const publicClient = {
  /**
   * Get public videos
   */
  getVideos: async (filters?: PublicVideosFilters): Promise<PaginatedResponse<Video>> => {
    const data = await apiClient.get<{
      videos?: PaginatedResponse<Video>;
    }>('/public/videos', {
      params: filters,
    });
    return data.videos || (data as unknown as PaginatedResponse<Video>);
  },

  /**
   * Get videos for map display with optional pagination
   */
  getMapVideos: async (filters?: MapVideosFilters): Promise<MapVideosPaginatedResponse> => {
    const data = await apiClient.get<{
      videos?: Video[];
      data?: Video[];
      pagination?: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
      };
      current_page?: number;
      last_page?: number;
      per_page?: number;
      total?: number;
    }>('/public/map-videos', { params: filters });

    const videos = data.videos || data.data || [];
    const perPage = data.pagination?.per_page || data.per_page || filters?.per_page || 20;

    return {
      videos,
      pagination: {
        current_page: data.pagination?.current_page || data.current_page || filters?.page || 1,
        last_page:
          data.pagination?.last_page ||
          data.last_page ||
          Math.ceil((data.pagination?.total || data.total || videos.length) / perPage),
        per_page: perPage,
        total: data.pagination?.total || data.total || videos.length,
        from: data.pagination?.from || 0,
        to: data.pagination?.to || 0,
      },
    };
  },

  /**
   * Get all available countries for filtering
   * Returns simple string array when simple_list=true
   */
  getCountries: async (params?: { simple_list?: boolean }): Promise<CountriesResponse> => {
    const response = await apiClient.get<CountriesResponse>('/public/countries', {
      params: { all: true, ...params },
    });
    return response;
  },

  /**
   * Get all available tags for filtering
   * Returns array of tag objects with id, name, and count
   */
  getTags: async (): Promise<TagOption[]> => {
    const response = await apiClient.get<{ data: TagOption[] }>('/public/tags', {
      params: { all: true },
    });
    return response.data || [];
  },

  /**
   * Get all available categories for filtering
   * Returns simple string array when simple_list=true
   */
  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get<{ data: string[] }>('/public/categories', {
      params: { simple_list: true },
    });
    return response.data || [];
  },

  // ── Freemium endpoints ──────────────────────────────────────────────

  /**
   * Get paginated public videos, optionally filtered by channel.
   */
  getPublicVideos: async (filters?: {
    creators?: number;
    page?: number;
    per_page?: number;
  }): Promise<PublicVideosResponse> => {
    const data = await apiClient.get<{
      data?: { videos?: Video[]; pagination?: PublicVideosResponse['pagination'] };
      videos?: Video[];
      pagination?: PublicVideosResponse['pagination'];
    }>('/public/public-videos', { params: filters });

    const nested =
      data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    const videos = ('videos' in nested ? nested.videos : undefined) || [];
    const pagination = ('pagination' in nested ? nested.pagination : undefined) || {
      total: videos.length,
      per_page: filters?.per_page || 20,
      current_page: filters?.page || 1,
      last_page: 1,
      from: videos.length > 0 ? 1 : null,
      to: videos.length > 0 ? videos.length : null,
    };

    return { videos, pagination };
  },

  /**
   * Get a single public video for playback with 5 recommendations.
   * Returns 404 if the video is not public or not published.
   */
  getPublicVideoPlay: async (uuid: string): Promise<PublicVideoPlayResponse> => {
    const data = await apiClient.get<{
      data?: { video?: Video; videos?: Video[] };
      video?: Video;
      videos?: Video[];
    }>(`/public/videos/${uuid}/play`);

    const nested =
      data.data && typeof data.data === 'object' && !Array.isArray(data.data) ? data.data : data;
    const video = ('video' in nested ? nested.video : undefined) as Video;
    const videos = ('videos' in nested ? nested.videos : undefined) || [];

    return { video, videos };
  },

  /**
   * Read-only metadata for a guest watch page. No view-tracking side effect.
   * Behind VIDEO_SPLIT_ENDPOINTS_ENABLED. Guest counterpart to videoClient.getMetadata.
   */
  getPublicMetadata: async (uuid: string): Promise<Video | null> => {
    const data = await apiClient.get<{ video?: Video; data?: Video }>(
      `/public/videos/${uuid}/metadata`
    );
    return data.video || data.data || (data as Video | null);
  },

  /**
   * Recommended videos for a guest watch page. Mirrors the authed counterpart;
   * BE returns the same Laravel paginator wrapper (`videos: { data: Video[] }`).
   */
  getPublicRecommended: async (uuid: string): Promise<Video[]> => {
    const data = await apiClient.get<{
      videos?: Video[] | { data?: Video[] };
      data?: Video[];
    }>(`/public/videos/${uuid}/recommended`);
    if (Array.isArray(data.videos)) return data.videos;
    if (data.videos && Array.isArray(data.videos.data)) return data.videos.data;
    return data.data || [];
  },

  /**
   * Anonymous view ping. BE logs to Axiom only, no DB write.
   * Same one-shot-per-session contract as the authed POST /view.
   */
  recordPublicView: async (uuid: string): Promise<{ ok: boolean }> => {
    return apiClient.post<{ ok: boolean }>(`/public/videos/${uuid}/view`);
  },

  // ── Public course endpoints ─────────────────────────────────────────

  /**
   * Get public courses catalog. No auth required. Paginated.
   * Returns courses with full media (thumbnails, trailer_url).
   */
  getCoursesCatalog: async (filters?: {
    sort_by?: 'newest' | 'trending' | 'old';
    per_page?: number;
    page?: number;
  }): Promise<{ courses: Course[]; pagination: PublicVideosResponse['pagination'] }> => {
    try {
      const data = await apiClient.get<{
        courses?: Course[];
        pagination?: PublicVideosResponse['pagination'];
      }>('/public/courses/catalog', { params: filters });

      return {
        courses: data.courses ?? [],
        pagination: data.pagination ?? {
          total: data.courses?.length ?? 0,
          per_page: filters?.per_page ?? 20,
          current_page: filters?.page ?? 1,
          last_page: 1,
          from: null,
          to: null,
        },
      };
    } catch (error) {
      captureError(error, { component: 'PublicClient', action: 'getCoursesCatalog' });
      return {
        courses: [],
        pagination: { total: 0, per_page: 20, current_page: 1, last_page: 1, from: null, to: null },
      };
    }
  },

  /**
   * Get a single public course by ID. No auth required.
   * Returns full course metadata with media. 404 if not found.
   */
  getCourseDetail: async (id: string | number): Promise<Course | null> => {
    try {
      const data = await apiClient.get<{
        course?: Course;
        data?: Course;
      }>(`/public/courses/${id}`);

      return data.course ?? data.data ?? null;
    } catch (error) {
      captureError(error, { component: 'PublicClient', action: 'getCourseDetail' });
      return null;
    }
  },

  /**
   * Get a single public bundle by ID. No auth required.
   * Returns bundle with primary_course and courses with media. 404 if not found or inactive.
   */
  getBundleDetail: async (id: string | number): Promise<CourseBundle | null> => {
    try {
      const data = await apiClient.get<{
        bundle?: CourseBundle;
        data?: { bundle?: CourseBundle };
      }>(`/public/courses/bundles/${id}`);

      return data.bundle ?? data.data?.bundle ?? null;
    } catch (error) {
      captureError(error, { component: 'PublicClient', action: 'getBundleDetail' });
      return null;
    }
  },
};
